import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Star as StarIcon,
  Comment as CommentIcon,
  AccessTime as AccessTimeIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ratingModulesAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#0F3D3E',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#0F3D3E',
  },
};

const DEFAULT_ADD_FORM = {
  displayName: '',
  intervalHours: 24,
};

const DEFAULT_EDIT_FORM = {
  moduleKey: '',
  displayName: '',
  isRatingEnabled: false,
  isCommentsEnabled: false,
  intervalHours: 24,
  isCronEnabled: false,
};

export default function RatingModulesSettings() {
  const { t } = useLanguage();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingKey, setUpdatingKey] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Add Dialog State
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState(DEFAULT_ADD_FORM);
  const [addSaving, setAddSaving] = useState(false);

  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(DEFAULT_EDIT_FORM);
  const [editSaving, setEditSaving] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Fetch all rating modules
  const fetchRatingModules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ratingModulesAPI.getRatingModules();
      const payload = response?.data?.payload || response?.data || [];
      if (Array.isArray(payload)) {
        // Sort by sortOrder
        const sorted = [...payload].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        setModules(sorted);
      } else {
        setModules([]);
      }
    } catch (err) {
      console.error('Error fetching rating modules:', err);
      showSnackbar(t('ratingModules.toastError') || 'Failed to load rating modules.', 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRatingModules();
  }, [fetchRatingModules]);

  // Update specific module settings via PUT API
  const handleUpdateModuleSettings = async (moduleKey, updatedValues) => {
    setUpdatingKey(moduleKey);
    try {
      const existing = modules.find((m) => m.moduleKey === moduleKey) || {};
      const payload = {
        isRatingEnabled: updatedValues.isRatingEnabled !== undefined ? updatedValues.isRatingEnabled : Boolean(existing.isRatingEnabled),
        isCommentsEnabled: updatedValues.isCommentsEnabled !== undefined ? updatedValues.isCommentsEnabled : Boolean(existing.isCommentsEnabled),
        intervalHours: updatedValues.intervalHours !== undefined ? Number(updatedValues.intervalHours) : (existing.intervalHours || 24),
        isCronEnabled: updatedValues.isCronEnabled !== undefined ? updatedValues.isCronEnabled : Boolean(existing.isCronEnabled),
      };

      await ratingModulesAPI.updateRatingModule(moduleKey, payload);

      // Optimistic update state locally
      setModules((prev) =>
        prev.map((mod) =>
          mod.moduleKey === moduleKey
            ? { ...mod, ...payload, settingsUpdatedUtc: new Date().toISOString() }
            : mod
        )
      );

      showSnackbar(t('ratingModules.toastUpdated') || 'Module settings updated successfully.', 'success');
    } catch (err) {
      console.error(`Error updating rating module ${moduleKey}:`, err);
      showSnackbar(t('ratingModules.toastError') || 'Failed to update module settings.', 'error');
      // Refetch on error to restore correct server state
      fetchRatingModules();
    } finally {
      setUpdatingKey(null);
    }
  };

  // Toggle handlers for individual switches
  const handleToggleRating = (module) => {
    handleUpdateModuleSettings(module.moduleKey, {
      isRatingEnabled: !module.isRatingEnabled,
    });
  };

  const handleToggleComments = (module) => {
    handleUpdateModuleSettings(module.moduleKey, {
      isCommentsEnabled: !module.isCommentsEnabled,
    });
  };

  const handleToggleCron = (module) => {
    handleUpdateModuleSettings(module.moduleKey, {
      isCronEnabled: !module.isCronEnabled,
    });
  };

  // Open Edit Modal
  const handleOpenEditDialog = async (module) => {
    try {
      // Fetch fresh data for key if needed
      const res = await ratingModulesAPI.getRatingModuleByKey(module.moduleKey);
      const data = res?.data?.payload || res?.data || module;
      setEditFormData({
        moduleKey: data.moduleKey,
        displayName: data.displayName || data.moduleKey,
        isRatingEnabled: Boolean(data.isRatingEnabled),
        isCommentsEnabled: Boolean(data.isCommentsEnabled),
        intervalHours: data.intervalHours || 24,
        isCronEnabled: Boolean(data.isCronEnabled),
      });
    } catch {
      setEditFormData({
        moduleKey: module.moduleKey,
        displayName: module.displayName || module.moduleKey,
        isRatingEnabled: Boolean(module.isRatingEnabled),
        isCommentsEnabled: Boolean(module.isCommentsEnabled),
        intervalHours: module.intervalHours || 24,
        isCronEnabled: Boolean(module.isCronEnabled),
      });
    }
    setEditDialogOpen(true);
  };

  const handleSaveEditDialog = async () => {
    if (!editFormData.moduleKey) return;
    setEditSaving(true);
    try {
      await handleUpdateModuleSettings(editFormData.moduleKey, {
        isRatingEnabled: editFormData.isRatingEnabled,
        isCommentsEnabled: editFormData.isCommentsEnabled,
        intervalHours: editFormData.intervalHours,
        isCronEnabled: editFormData.isCronEnabled,
      });
      setEditDialogOpen(false);
    } finally {
      setEditSaving(false);
    }
  };

  // Open Add Modal
  const handleOpenAddDialog = () => {
    setAddFormData({
      displayName: '',
      intervalHours: 24,
    });
    setAddDialogOpen(true);
  };

  // Create new module via POST API
  const handleSaveAddDialog = async () => {
    const displayName = addFormData.displayName.trim();
    if (!displayName) {
      showSnackbar(t('ratingModules.errDisplayNameRequired') || 'Please enter a Display Name.', 'warning');
      return;
    }
    const moduleKey = displayName.replace(/[^a-zA-Z0-9]/g, '');
    const nextSortOrder = (modules.length > 0 ? Math.max(...modules.map((m) => m.sortOrder || 0)) : 0) + 10;

    setAddSaving(true);
    try {
      const payload = {
        displayName,
        moduleKey,
        sortOrder: nextSortOrder,
        intervalHours: Number(addFormData.intervalHours) || 24,
      };

      await ratingModulesAPI.createRatingModule(payload);
      showSnackbar(t('ratingModules.toastCreated') || 'Module created successfully.', 'success');
      setAddDialogOpen(false);
      fetchRatingModules();
    } catch (err) {
      console.error('Error creating rating module:', err);
      showSnackbar(t('ratingModules.toastError') || 'Failed to create module.', 'error');
    } finally {
      setAddSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Bar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1.5}
      >
        <Box>
          <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
            {t('ratingModules.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('ratingModules.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            onClick={fetchRatingModules}
            disabled={loading}
            startIcon={<RefreshIcon />}
            sx={{
              color: '#0F3D3E',
              borderColor: '#0F3D3E',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                borderColor: '#165658',
                bgcolor: 'rgba(15, 61, 62, 0.04)',
              },
            }}
          >
            {t('contacts.refresh') || 'Refresh'}
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenAddDialog}
            startIcon={<AddIcon />}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              fontWeight: 600,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('ratingModules.addModule')}
          </Button>
        </Stack>
      </Box>

      {/* Cards View of Modules */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : modules.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <StarIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            {t('ratingModules.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            {t('ratingModules.emptySubtitle')}
          </Typography>
          <Button variant="contained" onClick={handleOpenAddDialog} startIcon={<AddIcon />} sx={{ bgcolor: '#0F3D3E' }}>
            {t('ratingModules.addModule')}
          </Button>
        </Paper>
      ) : (
        <>
          {/* Module Quick Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {modules.map((module) => {
              const isUpdating = updatingKey === module.moduleKey;

              return (
                <Grid item xs={12} sm={6} md={4} key={module.id || module.moduleKey}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid #E5E7EB',
                      transition: 'all 0.25s ease-in-out',
                      position: 'relative',
                      overflow: 'visible',
                      '&:hover': {
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                        borderColor: '#328284',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      {/* Top bar with Name & Edit button */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                          {module.displayName || module.moduleKey}
                        </Typography>
                        <Tooltip title={t('common.edit') || 'Edit'}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(module)}
                            sx={{ color: '#0F3D3E', bgcolor: '#F3F4F6', '&:hover': { bgcolor: '#E5E7EB' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Controls inside card */}
                      <Stack spacing={1.5}>
                        {/* Rating Enabled Toggle */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: '#F9FAFB',
                            p: 1.25,
                            borderRadius: 2,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <StarIcon sx={{ fontSize: 20, color: module.isRatingEnabled ? '#E4A853' : '#9CA3AF' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {t('ratingModules.ratingEnabled')}
                            </Typography>
                          </Stack>
                          <Switch
                            checked={Boolean(module.isRatingEnabled)}
                            onChange={() => handleToggleRating(module)}
                            disabled={isUpdating}
                            size="small"
                            sx={SWITCH_SX}
                          />
                        </Box>

                        {/* Comments Enabled Toggle */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: '#F9FAFB',
                            p: 1.25,
                            borderRadius: 2,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CommentIcon sx={{ fontSize: 20, color: module.isCommentsEnabled ? '#3B82F6' : '#9CA3AF' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {t('ratingModules.commentsEnabled')}
                            </Typography>
                          </Stack>
                          <Switch
                            checked={Boolean(module.isCommentsEnabled)}
                            onChange={() => handleToggleComments(module)}
                            disabled={isUpdating}
                            size="small"
                            sx={SWITCH_SX}
                          />
                        </Box>

                        {/* Cron Enabled Toggle */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            bgcolor: '#F9FAFB',
                            p: 1.25,
                            borderRadius: 2,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ScheduleIcon sx={{ fontSize: 20, color: module.isCronEnabled ? '#10B981' : '#9CA3AF' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {t('ratingModules.cronEnabled')}
                            </Typography>
                          </Stack>
                          <Switch
                            checked={Boolean(module.isCronEnabled)}
                            onChange={() => handleToggleCron(module)}
                            disabled={isUpdating}
                            size="small"
                            sx={SWITCH_SX}
                          />
                        </Box>

                        {/* Interval Hours */}
                        <Box sx={{ pt: 1, display: 'flex', alignItems: 'center' }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <AccessTimeIcon fontSize="small" sx={{ color: '#6B7280' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Interval: {module.intervalHours || 24}h
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}      {/* Dialog 1: Add New Rating Module */}
      <Dialog
        open={addDialogOpen}
        onClose={() => !addSaving && setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#0F3D3E',
            color: '#fff',
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff', flexGrow: 1 }}>
            {t('ratingModules.dialogAddTitle')}
          </Typography>
          <IconButton
            onClick={() => setAddDialogOpen(false)}
            disabled={addSaving}
            size="small"
            sx={{ color: '#ffffff', ml: 'auto', opacity: 0.85, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3.5 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label={t('ratingModules.fieldDisplayName')}
              value={addFormData.displayName}
              onChange={(e) => setAddFormData((prev) => ({ ...prev, displayName: e.target.value }))}
              fullWidth
              required
              placeholder="e.g. Contact, Event, Promotion"
            />
            <TextField
              label={t('ratingModules.fieldIntervalHours')}
              type="number"
              value={addFormData.intervalHours}
              onChange={(e) => setAddFormData((prev) => ({ ...prev, intervalHours: e.target.value }))}
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, pt: 1 }}>
          <Button
            onClick={() => setAddDialogOpen(false)}
            disabled={addSaving}
            sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAddDialog}
            disabled={addSaving}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
            }}
          >
            {addSaving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog 2: Edit Rating Module Settings */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editSaving && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#0F3D3E',
            color: '#fff',
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff', flexGrow: 1 }}>
            {t('ratingModules.dialogEditTitle')} — {editFormData.displayName}
          </Typography>
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            disabled={editSaving}
            size="small"
            sx={{ color: '#ffffff', ml: 'auto', opacity: 0.85, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3.5 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#F9FAFB' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.isRatingEnabled}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, isRatingEnabled: e.target.checked }))}
                    sx={SWITCH_SX}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('ratingModules.ratingEnabled')}
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#F9FAFB' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.isCommentsEnabled}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, isCommentsEnabled: e.target.checked }))}
                    sx={SWITCH_SX}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('ratingModules.commentsEnabled')}
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#F9FAFB' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.isCronEnabled}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, isCronEnabled: e.target.checked }))}
                    sx={SWITCH_SX}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('ratingModules.cronEnabled')}
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            <TextField
              label={t('ratingModules.fieldIntervalHours')}
              type="number"
              value={editFormData.intervalHours}
              onChange={(e) => setEditFormData((prev) => ({ ...prev, intervalHours: e.target.value }))}
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, pt: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={editSaving}
            sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditDialog}
            disabled={editSaving}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
            }}
          >
            {editSaving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
